import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { useInfiniteHits, useInstantSearch } from 'react-instantsearch';

import type {
  Position,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { InstantCardSearch } from '@sorare/core/src/components/search/InstantSearch';
import type { AnalyticTag } from '@sorare/core/src/components/search/InstantSearch/types';
import type {
  PrintablePlayerSortName,
  ShortSortName,
} from '@sorare/core/src/contexts/config';
import { SearchCardsProvider as SearchCardsContextProvider } from '@sorare/core/src/contexts/searchCards/Provider';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { joinFiltersWithOr } from '@sorare/core/src/lib/algolia';

import { useDefaultPrintablePlayersSearchClient } from '@sorare/marketplace/src/hooks/search/useDefaultPrintablePlayersSearchClient';
import type { BaseQueryVariables } from '@sorare/marketplace/src/hooks/search/usePrintablePlayersSearchClient';
import type { extractQueryGeneric } from '@sorare/marketplace/src/lib/searchClient';
import type { Props as AdvancedCardSearchProps } from '@sorare/marketplace/src/searchCards/AdvancedCardSearch/types';

import type { AdvancedMarket_searchPlayers } from './__generated__/index.graphql.ts';

type ResultOf<T> =
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  T extends TypedDocumentNode<infer ResultType, infer VariablesType>
    ? ResultType
    : never;

type AdvancedMarketBaseQueryVariables = BaseQueryVariables & {
  rarity: Rarity;
};

type AdvancedMarketBaseQuery<
  Variables extends AdvancedMarketBaseQueryVariables,
> = TypedDocumentNode<
  {
    searchPlayers: AdvancedMarket_searchPlayers;
  },
  Variables
>;
type Props<
  Data extends ResultOf<
    AdvancedMarketBaseQuery<AdvancedMarketBaseQueryVariables>
  >,
  Variables,
> = Omit<
  AdvancedCardSearchProps,
  'sport' | 'children' | 'cardFilters' | 'advancedCardFilters' | 'sorts'
> & {
  positions: Position[];
  children: (arg: {
    cards: NonNullable<
      Data['searchPlayers']['hits'][number]['anyPlayer']['anyCardWithLivePrimaryOffer']
    >[];
    players: Data['searchPlayers']['hits'][number]['anyPlayer'][];
    sorts: PrintablePlayerSortName[];
    loading: boolean;
  }) => ReactNode;
  analyticsTags?: AnalyticTag[];
  filters?: string[];
  query: TypedDocumentNode<Data, Variables & BaseQueryVariables>;
  variables: Variables;
  sorts: ShortSortName[];
  availableSorts: PrintablePlayerSortName[];
  onFavoriteFilterChange?: (v: boolean) => void;
  skip?: boolean;
};

const InnerComponent = <
  Data extends ResultOf<
    AdvancedMarketBaseQuery<AdvancedMarketBaseQueryVariables>
  >,
>({
  children,
}: {
  children: (arg: {
    cards: NonNullable<
      Data['searchPlayers']['hits'][number]['anyPlayer']['anyCardWithLivePrimaryOffer']
    >[];
    players: Data['searchPlayers']['hits'][number]['anyPlayer'][];
    loading: boolean;
  }) => ReactNode;
}) => {
  const { items } = useInfiniteHits<Data['searchPlayers']['hits'][number]>();
  const { status } = useInstantSearch();

  const loading = status === 'loading' || status === 'stalled';

  return (
    <div>
      {children({
        cards: items
          .map(({ anyPlayer }) => anyPlayer.anyCardWithLivePrimaryOffer)
          .filter(Boolean),
        players: items.map(({ anyPlayer }) => anyPlayer),
        loading,
      })}
    </div>
  );
};

export const AdvancedMarket = <
  Data extends ResultOf<
    AdvancedMarketBaseQuery<AdvancedMarketBaseQueryVariables>
  >,
  Variables extends Record<string, unknown> | undefined,
>({
  positions,
  query,
  variables,
  sorts,
  availableSorts,
  children,
  analyticsTags,
  filters,
  skip,
  ...rest
}: Props<Data, Variables>) => {
  const { sport, sportAnalyticsTag } = useSportContext();
  const indexes = sorts.length > 0 ? [sorts.join(',')] : [availableSorts[0]];

  const searchClient = useDefaultPrintablePlayersSearchClient(
    query,
    {
      needsComposeTeamBench: true,
      onlyFollowed: false,
      variables,
    },
    (
      data: extractQueryGeneric<
        TypedDocumentNode<Data, Variables & BaseQueryVariables>
      >
    ) => data.searchPlayers
  );

  return (
    <div>
      <SearchCardsContextProvider commonMarket={false}>
        <InstantCardSearch
          {...(indexes && { indexes })}
          availableSorts={availableSorts}
          analyticsTags={[sportAnalyticsTag, ...(analyticsTags || [])]}
          sport={sport}
          defaultFilters={[
            ...(filters || []),
            joinFiltersWithOr(positions.map(p => `position:${p}`)),
          ]}
          searchClient={searchClient}
          defaultHitsPerPage={50}
          {...(rest.attributesToRetrieve && {
            attributesToRetrieve: rest.attributesToRetrieve,
          })}
          skip={skip}
        >
          <InnerComponent>
            {({ cards, players, loading }): ReactNode =>
              children({ cards, players, sorts: availableSorts, loading })
            }
          </InnerComponent>
        </InstantCardSearch>
      </SearchCardsContextProvider>
    </div>
  );
};

AdvancedMarket.fragments = {
  searchPlayers: gql`
    fragment AdvancedMarket_searchPlayers on SearchPlayers {
      hits {
        id
        anyPlayer {
          slug
          anyCardWithLivePrimaryOffer(rarity: $rarity) {
            slug
          }
        }
      }
      ...useDefaultPrintablePlayersSearchClient_searchPrintablePlayers
    }
    ${useDefaultPrintablePlayersSearchClient.fragments.searchPrintablePlayers}
  ` as TypedDocumentNode<AdvancedMarket_searchPlayers>,
};
